// === React & React Hooks ===
import { memo, ReactNode, useMemo, useState } from 'react';

// === UI Components ===
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    SelectItem,
    Textarea,
} from '@heroui/react';

interface SelectOption {
    key: string;
    label: string;
}

interface SubmitMessageModalProps {
    onSubmit: (data: { selectValue: string; messageText: string }) => void;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    header: string;
    subheader: ReactNode;
    selectName?: string;
    selectOptions?: SelectOption[];
    messagePlaceholder?: string;
}

export const SubmitMessageModal = memo(
    ({
        header,
        subheader,
        selectName = '',
        selectOptions = [],
        messagePlaceholder = '',
        onSubmit,
        isOpen,
        onOpenChange,
    }: SubmitMessageModalProps) => {
        const [messageText, setMessageText] = useState('');
        const [selectValue, setSelectValue] = useState<string>('');

        const requiresSelection = useMemo(
            () => Boolean(selectName && selectOptions.length > 0),
            [selectName, selectOptions],
        );

        const handleSubmit = () => {
            if (!messageText || (requiresSelection && !selectValue)) return;

            const selectValid = requiresSelection ? !!selectValue : true;
            if (!selectValid || !messageText) return;
            onSubmit({
                selectValue: requiresSelection ? selectValue : '',
                messageText,
            });
            setMessageText('');
            setSelectValue('');
            onOpenChange(false);
        };

        const canSubmit = useMemo(() => {
            const selectValid = requiresSelection ? !!selectValue : true;
            return selectValid && !!messageText;
        }, [selectValue, messageText, requiresSelection]);

        return (
            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                backdrop="opaque"
                classNames={{
                    base: 'py-4 px-4 text-foreground',
                    backdrop: 'bg-foreground/20',
                }}
            >
                <ModalContent>
                    {() => (
                        <>
                            <ModalHeader className="flex flex-col gap-4 text-foreground">
                                <h3 className="text-center text-2xlarge font-bold">{header}</h3>
                            </ModalHeader>

                            <ModalBody>
                                <div className="place-items-center rounded-large">
                                    <div className="flex w-full flex-col justify-start gap-1 text-mini">
                                        {subheader}
                                    </div>

                                    <div className="w-full">
                                        {selectName && selectOptions.length > 0 && (
                                            <Select
                                                label={selectName}
                                                placeholder="Select One"
                                                items={selectOptions}
                                                onSelectionChange={(key) =>
                                                    setSelectValue(Array.from(key)[0] as string)
                                                }
                                                aria-label={selectName}
                                                className="mt-4"
                                                isRequired
                                            >
                                                {(option: SelectOption) => (
                                                    <SelectItem key={option.key}>
                                                        {option.label}
                                                    </SelectItem>
                                                )}
                                            </Select>
                                        )}
                                        <Textarea
                                            label="Message"
                                            placeholder={messagePlaceholder}
                                            value={messageText}
                                            onValueChange={setMessageText}
                                            className="mt-4"
                                            minRows={5}
                                            isRequired
                                        />
                                    </div>
                                </div>
                            </ModalBody>

                            <ModalFooter>
                                <Button
                                    color="primary"
                                    onPress={handleSubmit}
                                    isDisabled={!canSubmit}
                                >
                                    Submit
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        );
    },
);

SubmitMessageModal.displayName = 'SubmitMessageModal';
